/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

/** Portals that expose an Edit action when connected with a live post/listing. Instagram is excluded (Meta Graph cannot edit posts). */
export const EDITABLE_PORTALS = new Set(['olx', 'facebook']);

/** Portals where removing/changing images requires confirming a post recreate. */
export const RECREATE_ON_EDIT_PORTALS = new Set(['facebook']);

/** Where a requested Lead Ad is: being created by the worker, running, or not created. */
export type LeadAdStatus = 'pending' | 'created' | 'failed';

/** A boost running on a post, as remembered in the publications index. */
export interface RowBoost {
  ad_id: string;
  daily_budget_cents?: number;
  duration_days?: number;
  /** Set once the boost was paused from Housien. */
  paused_at?: string;
}

/** A single portal's live publication state for a property. */
export interface PortalPublication {
  status: string;
  post_ids?: string[];
  listing_ids?: string[];
  post_id?: string;
  listing_id?: string;
  ad_id?: string;
  published_at?: string;
  /**
   * When this entry was last enqueued for the publish worker; only present
   * while `status` is "queued". Unlike `published_at` (kept across a retry or
   * edit of an already-published post), this is always the latest enqueue.
   */
  queued_at?: string;
  permalink?: string;
  lead_ad_status?: LeadAdStatus;
  lead_ad_error?: string;
}

/** A display-ready row representing one portal's publication for a property. */
export interface PublicationRow {
  key: string;
  portal: string;
  label: string;
  status: string;
  published_at?: string;
  post_id?: string;
  listing_id?: string;
  ad_id?: string;
  /** The Lead Ad created with this post, kept apart from ad_id (which blocks boosting) -- only used to link to it. */
  lead_ad_id?: string;
  /** True once the Lead Ad was paused from Housien. */
  lead_ad_paused?: boolean;
  permalink?: string;
  lead_ad_status?: LeadAdStatus;
  lead_ad_error?: string;
  boosts?: RowBoost[];
}

/** Props for the PublicationsCenter panel (slide-over/modal). */
export interface PublicationsCenterProps {
  propertyId: string;
  agencyId: string;
  onClose: () => void;
  propertyImages?: string[];
}
